import React, { useState, useEffect } from 'react';
import { api } from '../../utils/requestInterceptor';
import { toast } from 'react-hot-toast';
import { PlusIcon, XMarkIcon, UserIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import './raciTooltips.css';
import useRaciAssignments from '../../hooks/useRaciAssignments';

// RACI role descriptions for tooltips
const RACI_DESCRIPTIONS = {
  A: 'These are the people responsible for the overall project',
  C: 'These are the people that need to be consulted for the project',
  I: 'These are the people that need to be kept informed'
};

// RACI role colors
const RACI_COLORS = {
  A: 'bg-blue-100 text-blue-800 border-blue-300',
  C: 'bg-purple-100 text-purple-800 border-purple-300',
  I: 'bg-yellow-100 text-yellow-800 border-yellow-300'
};

const ProjectRaciAssignments = ({ projectId }) => {
  const [users, setUsers] = useState([]);
  const [addingRole, setAddingRole] = useState(null); // 'A', 'C', 'I' or null
  const [showModal, setShowModal] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  
  // Use our custom RACI assignments hook
  const { 
    projectAssignments: assignments, 
    updateAssignment,
    refreshAssignments,
    isLoading: loading, 
    error 
  } = useRaciAssignments(projectId);

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.get('/auth/users');
        if (response.data && response.data.users) {
          setUsers(response.data.users);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    fetchUsers();
  }, []);

  // Update handleAddAssignment to support multiple users
  const handleAddAssignment = async (userIds, roleType) => {
    if (!roleType || !userIds || userIds.length === 0) return;
    
    try {
      // Create an array of promises for each user assignment
      const assignmentPromises = userIds.map(userId => 
        updateAssignment('project', projectId, roleType, userId, true)
      );
      
      // Wait for all assignments to complete
      await Promise.all(assignmentPromises);
      
      // Refresh assignments to ensure UI is up to date
      refreshAssignments();
      
      // Reset form
      setAddingRole(null);
      setShowModal(false);
      setSelectedUserIds([]);
      
      toast.success(`Users assigned as ${roleType} successfully`);
    } catch (error) {
      console.error('Error adding assignments:', error);
      toast.error('Failed to add assignments');
    }
  };

  // Add function to toggle user selection
  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prevSelected => 
      prevSelected.includes(userId)
        ? prevSelected.filter(id => id !== userId)
        : [...prevSelected, userId]
    );
  };

  // Remove an assignment
  const handleRemoveAssignment = async (assignmentId, userId, roleType) => {
    try {
      await updateAssignment('project', projectId, roleType, userId, false);
      
      // Our hook already updates the local state, but refresh to be sure
      refreshAssignments();
      
      toast.success('Assignment removed successfully');
    } catch (error) {
      console.error('Error removing assignment:', error);
      toast.error('Failed to remove assignment');
    }
  };

  // Filter out users who are already assigned to the selected role
  const getAvailableUsers = () => {
    if (!addingRole || !assignments) return [];
    
    const assignedUserIds = assignments[addingRole]?.map(a => a.user_id) || [];
    return users.filter(user => !assignedUserIds.includes(user.id));
  };

  if (loading) {
    return (
      <div className="animate-pulse p-4">
        <div className="h-4 bg-gray-200 rounded mb-4 w-3/4"></div>
        <div className="h-4 bg-gray-200 rounded mb-4 w-1/2"></div>
        <div className="h-4 bg-gray-200 rounded mb-4 w-2/3"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-700 bg-red-100 rounded-md">
        <p>{error}</p>
        <button 
          onClick={() => window.location.reload()}
          className="mt-2 text-red-700 underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
      {/* Modal for user selection */}
      {showModal && addingRole && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative mx-auto p-5 border border-gray-700 w-96 shadow-lg rounded-md bg-gray-800">
            <div className="flex justify-between items-center border-b border-gray-700 pb-3">
              <h3 className="text-lg font-medium text-gray-100">
                Select users to assign as {addingRole === 'A' ? 'Accountable' : 
                                          addingRole === 'C' ? 'Consulted' : 'Informed'}
              </h3>
              <button 
                onClick={() => {
                  setShowModal(false);
                  setSelectedUserIds([]);
                }}
                className="text-gray-400 hover:text-gray-200"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mt-4 max-h-60 overflow-y-auto">
              {getAvailableUsers().length > 0 ? (
                getAvailableUsers().map(user => (
                  <div
                    key={user.id}
                    className={`w-full text-left px-4 py-3 rounded-md flex items-center mb-1 cursor-pointer ${
                      selectedUserIds.includes(user.id) ? 'bg-gray-700' : 'hover:bg-gray-700'
                    }`}
                    onClick={() => toggleUserSelection(user.id)}
                  >
                    <div className="flex-shrink-0 h-8 w-8 rounded-full bg-indigo-600 flex items-center justify-center text-white mr-3">
                      {user.name.charAt(0)}
                    </div>
                    <span className="text-sm font-medium text-gray-100 flex-grow">{user.name}</span>
                    <div className={`h-5 w-5 rounded border ${
                      selectedUserIds.includes(user.id) 
                        ? 'bg-blue-500 border-blue-500 flex items-center justify-center' 
                        : 'border-gray-500'
                    }`}>
                      {selectedUserIds.includes(user.id) && (
                        <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="px-4 py-3 text-sm text-gray-400 italic">All users already assigned</p>
              )}
            </div>
            
            <div className="mt-4 flex justify-between border-t border-gray-700 pt-3">
              <div className="text-sm text-gray-400">
                {selectedUserIds.length} user{selectedUserIds.length !== 1 ? 's' : ''} selected
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setShowModal(false);
                    setSelectedUserIds([]);
                  }}
                  className="px-4 py-2 bg-gray-700 text-gray-200 text-sm font-medium rounded-md hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleAddAssignment(selectedUserIds, addingRole)}
                  disabled={selectedUserIds.length === 0}
                  className={`px-4 py-2 text-white text-sm font-medium rounded-md ${
                    selectedUserIds.length > 0 
                      ? 'bg-blue-600 hover:bg-blue-700' 
                      : 'bg-blue-800 bg-opacity-50 cursor-not-allowed'
                  }`}
                >
                  Assign
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg leading-6 font-medium text-gray-100">
          Project Assignments
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          Manage who is Accountable, Consulted, and Informed for this project
        </p>
      </div>
      
      <div className="border-t border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 relative">          
          {/* Accountable (A) column */}
          <div className="border-b md:border-b-0 md:border-r border-gray-200 relative overflow-visible">
            <div className="flex justify-between items-center px-4 py-3 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center">
                <span className={`tooltip mr-2 px-2.5 py-1 rounded-full text-xs font-medium relative cursor-help ${RACI_COLORS.A}`}>
                  A
                  <span className="tooltiptext">{RACI_DESCRIPTIONS.A}</span>
                </span>
                <span className="font-medium text-sm text-gray-100">Accountable</span>
              </div>
              <div className="relative flex-shrink-0">
                <button
                  onClick={() => {
                    setAddingRole('A');
                    setShowModal(true);
                  }}
                  className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
                  title="Add Accountable"
                >
                  <PlusIcon className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </div>
            
            <ul className="divide-y divide-gray-200">
              {assignments.A.length === 0 ? (
                <li className="px-4 py-3 text-sm text-gray-500 italic">No users assigned</li>
              ) : (
                assignments.A.map(assignment => (
                  <li 
                    key={assignment.id}
                    className="px-4 py-3 flex justify-between items-center hover:bg-gray-50"
                  >
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                        {assignment.user_name ? assignment.user_name.charAt(0) : <UserIcon className="h-5 w-5" />}
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-100">{assignment.user_name}</p>
                      </div>
                    </div>
                    <button 
                      onClick={() => handleRemoveAssignment(assignment.id, assignment.user_id, 'A')}
                      className="text-gray-400 hover:text-red-500"
                      title="Remove assignment"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>
          
          {/* Consulted (C) column */}
          <div className="border-b md:border-b-0 md:border-r border-gray-200 relative overflow-visible">
            <div className="flex justify-between items-center px-4 py-3 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center">
                <span className={`tooltip mr-2 px-2.5 py-1 rounded-full text-xs font-medium relative cursor-help ${RACI_COLORS.C}`}>
                  C
                  <span className="tooltiptext">{RACI_DESCRIPTIONS.C}</span>
                </span>
                <span className="font-medium text-sm text-gray-100">Consulted</span>
              </div>
              <div className="relative flex-shrink-0">
                <button
                  onClick={() => {
                    setAddingRole('C');
                    setShowModal(true);
                  }}
                  className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none"
                  title="Add Consulted"
                >
                  <PlusIcon className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </div>
            
            <ul className="divide-y divide-gray-200">
              {assignments.C.length === 0 ? (
                <li className="px-4 py-3 text-sm text-gray-500 italic">No users assigned</li>
              ) : (
                assignments.C.map(assignment => (
                  <li 
                    key={assignment.id}
                    className="px-4 py-3 flex justify-between items-center hover:bg-gray-50"
                  >
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                        {assignment.user_name ? assignment.user_name.charAt(0) : <UserIcon className="h-5 w-5" />}
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-100">{assignment.user_name}</p>
                      </div>
                    </div>
                    <button 
                      onClick={() => handleRemoveAssignment(assignment.id, assignment.user_id, 'C')}
                      className="text-gray-400 hover:text-red-500"
                      title="Remove assignment"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>
          
          {/* Informed (I) column */}
          <div className="relative overflow-visible">
            <div className="flex justify-between items-center px-4 py-3 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center">
                <span className={`tooltip mr-2 px-2.5 py-1 rounded-full text-xs font-medium relative cursor-help ${RACI_COLORS.I}`}>
                  I
                  <span className="tooltiptext">{RACI_DESCRIPTIONS.I}</span>
                </span>
                <span className="font-medium text-sm text-gray-100">Informed</span>
              </div>
              <div className="relative flex-shrink-0">
                <button
                  onClick={() => {
                    setAddingRole('I');
                    setShowModal(true);
                  }}
                  className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none"
                  title="Add Informed"
                >
                  <PlusIcon className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </div>
            
            <ul className="divide-y divide-gray-200">
              {assignments.I.length === 0 ? (
                <li className="px-4 py-3 text-sm text-gray-500 italic">No users assigned</li>
              ) : (
                assignments.I.map(assignment => (
                  <li 
                    key={assignment.id}
                    className="px-4 py-3 flex justify-between items-center hover:bg-gray-50"
                  >
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                        {assignment.user_name ? assignment.user_name.charAt(0) : <UserIcon className="h-5 w-5" />}
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-100">{assignment.user_name}</p>
                      </div>
                    </div>
                    <button 
                      onClick={() => handleRemoveAssignment(assignment.id, assignment.user_id, 'I')}
                      className="text-gray-400 hover:text-red-500"
                      title="Remove assignment"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectRaciAssignments; 